import React from 'react';
import { useTranslation } from 'react-i18next';

import { Duration } from '@client/components/price/Range/Duration';
import Rebate from '@client/components/price/Rebate';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { flexLayout, type TFlexLayoutProps } from '@client/utils/layout.util';
import {
  CNsDiscount,
  CNsDuration,
  CNsPrice,
  CNsTrial,
  type ECurrency,
  type EDurationTypes,
  type EDiscountType,
} from '@common/types';

import type { FormInstance } from 'antd';

type TFeatureTrialProps = {
  formRef: FormInstance<any>;
  disabled: boolean;
  loading: boolean;
  durationTypes?: (keyof typeof EDurationTypes)[];
  currencies?: (keyof typeof ECurrency)[];
  discountTypes?: (keyof typeof EDiscountType)[];
  renderScheduler: (readOnlyFields?: string[]) => React.ReactNode;
};

export const FeatureTrial: React.FC<TFeatureTrialProps> = (props) => {
  const { t } = useTranslation();
  const { ability } = useAuth();

  const {
    formRef,
    disabled,
    loading,
    durationTypes = [],
    currencies = [],
    discountTypes,
    renderScheduler,
  } = props;

  const discountProps = {
    formRef,
    disabled,
    currencies,
    durationTypes,
    discountTypes,
    loading,
  };

  return (
    <Rebate.Discount
      {...discountProps}
      prefix={[CNsTrial, CNsPrice]}
      header={t('price.priceInfo', { type: t('price.trial') })}
      discountHeader={t('price.discountInfo', { header: t('price.trial') })}
      namespace={CNsDiscount}
      priceMin={0}
      renderScheduler={renderScheduler}
      readOnlyFields={['currency', 'originalPrice']}
      childrenColProps={flexLayout.halfColumn as unknown as TFlexLayoutProps}
      canBeDiscounted={ability.can(
        EAbilityActions.UPDATE,
        `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.DISCOUNT}`,
      )}
      canBePeriodic={ability.can(
        EAbilityActions.UPDATE,
        `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.PERIODIC}`,
      )}
    >
      <Duration
        label={t('price.trialDuration')}
        disabled={disabled}
        prefix={[CNsTrial]}
        namespace={CNsDuration}
        exclude={['HOUR', 'FOREVER']}
        required={true}
        durationTypes={durationTypes}
      />
    </Rebate.Discount>
  );
};
